import { Workflow } from 'api/types'
import { parseIso } from 'lib/format'

export type PrintLocale = 'ar' | 'en'

export const PRINT_WORKFLOW: Record<PrintLocale, Record<Workflow, string>> = {
  ar: {
    APPROVED: 'معتمد',
    DRAFT: 'مسودة',
    EMPTY: 'لم يبدأ',
    RETURNED: 'تقرير للمراجعة',
    SUBMITTED: 'بانتظار الاعتماد',
  },
  en: {
    APPROVED: 'Approved',
    DRAFT: 'Draft',
    EMPTY: 'Not started',
    RETURNED: 'Report for review',
    SUBMITTED: 'Pending approval',
  },
}

const WEEKDAY_EN = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

/** Day name independent of the backend-localized `report.dayName`, so EN mode never shows Arabic text. */
export const printWeekday = (locale: PrintLocale, reportDate: string, fallbackArabic: string) =>
  locale === 'ar' ? fallbackArabic : WEEKDAY_EN[parseIso(reportDate).getUTCDay()]

/** `10 Oct 2026` in English, `DD/MM/YYYY` in Arabic (matches the rest of the Arabic-only app). */
export const printDate = (locale: PrintLocale, iso?: string | null) => {
  if (!iso) return ''
  if (locale === 'ar') {
    const [year, month, day] = iso.slice(0, 10).split('-')
    return `${day}/${month}/${year}`
  }
  return new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC', year: 'numeric' }).format(new Date(iso))
}

export const printDateTime = (locale: PrintLocale, value?: string | null) => {
  if (!value) return ''
  return new Intl.DateTimeFormat(locale === 'ar' ? 'ar-JO' : 'en-GB', {
    dateStyle: 'medium',
    numberingSystem: 'latn',
    timeStyle: 'short',
    timeZone: 'Asia/Amman',
  }).format(new Date(value))
}

interface PrintLabels {
  close: string
  print: string
  loading: string
  loadError: string
  reportTitle: string
  infoProject: string
  infoTender: string
  infoConsultant: string
  infoDay: string
  infoWeather: string
  infoPreparedBy: string
  staffTitle: string
  staffOnSite: (count: string) => string
  staffJobTitles: (count: number) => string
  colJobTitle: string
  colCount: string
  colNameNote: string
  total: string
  labTitle: string
  labTests: (count: number) => string
  colTestType: string
  colLocation: string
  colResult: string
  colLabResult: string
  materialsTitle: string
  materialsDeliveries: (count: number) => string
  colMaterial: string
  colQuantity: string
  colDeliveryNote: string
  equipmentTitle: string
  equipmentUnits: (count: string) => string
  colMachine: string
  worksTitle: string
  worksItems: (items: number, photos: number) => string
  colIndex: string
  colWorkDescription: string
  visitsTitle: string
  visitsCount: (count: number) => string
  colVisitingParty: string
  colReason: string
  colMinutes: string
  supervisionTitle: string
  supervisionMeta: (notes: number, files: number) => string
  fileLabel: string
  workflowTitle: string
  submittedBy: string
  approvedBy: string
  footerGenerated: (date: string) => string
  dash: string
}

export const PP_LABELS: Record<PrintLocale, PrintLabels> = {
  ar: {
    approvedBy: 'اعتمده',
    close: 'إغلاق',
    colCount: 'العدد',
    colDeliveryNote: 'إشعار التسليم',
    colIndex: '#',
    colJobTitle: 'المسمى الوظيفي',
    colLabResult: 'ملف المختبر',
    colLocation: 'الموقع / العنصر',
    colMachine: 'المعدة',
    colMaterial: 'المادة',
    colNameNote: 'الاسم / ملاحظة',
    colQuantity: 'الكمية',
    colReason: 'السبب',
    colMinutes: 'محضر الاجتماع',
    colResult: 'النتيجة',
    colTestType: 'نوع الفحص',
    colVisitingParty: 'الجهة الزائرة',
    colWorkDescription: 'وصف العمل',
    dash: '—',
    equipmentTitle: 'المعدات',
    equipmentUnits: (count) => `${count} وحدة`,
    fileLabel: 'ملف',
    footerGenerated: (date) => `أُنشئ بتاريخ ${date} بواسطة JoPath`,
    infoConsultant: 'استشاري الإشراف',
    infoDay: 'اليوم',
    infoPreparedBy: 'أعده',
    infoProject: 'المشروع',
    infoTender: 'رقم العطاء',
    infoWeather: 'الطقس',
    labTests: (count) => `${count} فحص`,
    labTitle: 'الفحوصات المخبرية',
    loadError: 'تعذر تحميل التقرير',
    loading: 'جارٍ التحميل…',
    materialsDeliveries: (count) => `${count} توريد`,
    materialsTitle: 'المواد الموردة',
    print: 'طباعة / حفظ PDF',
    reportTitle: 'تقرير عمل يومي',
    staffJobTitles: (count) => `${count} مسمى`,
    staffOnSite: (count) => `${count} في الموقع`,
    staffTitle: 'الكوادر',
    submittedBy: 'أرسله',
    supervisionMeta: (notes, files) => [notes ? `${notes} ملاحظة` : null, files ? `${files} ملف` : null].filter(Boolean).join(' · '),
    supervisionTitle: 'ملاحظات الإشراف',
    total: 'الإجمالي',
    visitsCount: (count) => `${count} زيارة`,
    visitsTitle: 'الزيارات',
    workflowTitle: 'سير العمل',
    worksItems: (items, photos) => (photos ? `${items} بند · ${photos} صورة` : `${items} بند`),
    worksTitle: 'وصف الأعمال',
  },
  en: {
    approvedBy: 'Approved by',
    close: 'Close',
    colCount: 'Count',
    colDeliveryNote: 'Delivery note',
    colIndex: '#',
    colJobTitle: 'Job title',
    colLabResult: 'Lab result',
    colLocation: 'Location / element',
    colMachine: 'Machine',
    colMaterial: 'Material',
    colNameNote: 'Name / note',
    colQuantity: 'Quantity',
    colReason: 'Reason',
    colMinutes: 'Minutes of meeting',
    colResult: 'Result',
    colTestType: 'Test type',
    colVisitingParty: 'Visiting party',
    colWorkDescription: 'Work description',
    dash: '—',
    equipmentTitle: 'Equipment',
    equipmentUnits: (count) => `${count} units`,
    fileLabel: 'File',
    footerGenerated: (date) => `Generated ${date} by JoPath`,
    infoConsultant: 'Supervision consultant',
    infoDay: 'Day',
    infoPreparedBy: 'Prepared by',
    infoProject: 'Project',
    infoTender: 'Tender no.',
    infoWeather: 'Weather',
    labTests: (count) => `${count} tests`,
    labTitle: 'Lab tests',
    loadError: 'Could not load the report',
    loading: 'Loading…',
    materialsDeliveries: (count) => `${count} deliveries`,
    materialsTitle: 'Materials',
    print: 'Print / Save PDF',
    reportTitle: 'Daily site report',
    staffJobTitles: (count) => `${count} job titles`,
    staffOnSite: (count) => `${count} on site`,
    staffTitle: 'Staff',
    submittedBy: 'Submitted by',
    supervisionMeta: (notes, files) => [notes ? `${notes} notes` : null, files ? `${files} files` : null].filter(Boolean).join(' · '),
    supervisionTitle: 'Supervision notes',
    total: 'Total',
    visitsCount: (count) => `${count} visits`,
    visitsTitle: 'Visits',
    workflowTitle: 'Workflow',
    worksItems: (items, photos) => (photos ? `${items} items · ${photos} photos` : `${items} items`),
    worksTitle: 'Works',
  },
}
